'use client';

// ═══════════════════════════════════════════════════════════════
// useProducts Hook
// Access products from the theme context
// ═══════════════════════════════════════════════════════════════

import { useMemo } from 'react';
import { useThemeContext } from '../data-provider';
import type { Product } from '../theme-contract';

interface UseProductsReturn {
    /** All products */
    products: Product[];

    /** Loading state (for future async fetching) */
    isLoading: boolean;

    /** Get a product by slug */
    getProductBySlug: (slug: string) => Product | undefined;

    /** Get products by collection slug */
    getProductsByCollection: (collectionSlug: string) => Product[];

    /** Search products by name */
    searchProducts: (query: string) => Product[];

    /** Featured products (first 6) */
    featuredProducts: Product[];
}

/**
 * Hook to access products from theme context
 * Provides utility functions for filtering and searching
 */
export function useProducts(): UseProductsReturn {
    const { initialProducts } = useThemeContext();

    const products = initialProducts || [];

    const getProductBySlug = useMemo(() => {
        return (slug: string): Product | undefined => {
            return products.find(p => p.slug === slug);
        };
    }, [products]);

    const getProductsByCollection = useMemo(() => {
        return (collectionSlug: string): Product[] => {
            return products.filter(p =>
                p.collection_id === collectionSlug ||
                p.category_id === collectionSlug
            );
        };
    }, [products]);

    const searchProducts = useMemo(() => {
        return (query: string): Product[] => {
            const lowerQuery = query.toLowerCase();
            return products.filter(p =>
                p.name.toLowerCase().includes(lowerQuery) ||
                p.description?.toLowerCase().includes(lowerQuery)
            );
        };
    }, [products]);

    const featuredProducts = useMemo(() => {
        return products.slice(0, 6);
    }, [products]);

    return {
        products,
        isLoading: false,
        getProductBySlug,
        getProductsByCollection,
        searchProducts,
        featuredProducts,
    };
}

export default useProducts;
